import QRCode from 'qrcode';

import { useEffect, useRef } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { QR_COLORS } from '@/lib/qrColors';

import { getAppStoreConfig } from '@/config/appStore';
import { isMobile } from '@/utils/platform';

interface StoreQrCodesProps {
  className?: string;
}

type Store = 'ios' | 'android';

interface StoreQrProps {
  store: Store;
  url: string;
  label: string;
}

const STORE_LOGO_PATHS: Record<Store, string> = {
  ios: 'M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z',
  android:
    'M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.77-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z',
};

function StoreLogo({ store }: { store: Store }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      data-testid={`store-logo-${store}`}
      className="h-12 w-12 shrink-0 text-gray-700 dark:text-gray-200"
    >
      <path d={STORE_LOGO_PATHS[store]} />
    </svg>
  );
}

function StoreQr({ store, url, label }: StoreQrProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 96,
      margin: 1,
      color: QR_COLORS,
    }).catch(() => {
      /* canvas unsupported: the badges elsewhere still link to the store */
    });
  }, [url]);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`store-qr-link-${store}`}
      className="group flex flex-col items-center gap-2"
    >
      <StoreLogo store={store} />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        data-testid={`store-qr-${store}`}
        width={96}
        height={96}
        className="rounded-lg bg-white p-1 transition-shadow duration-200 group-hover:shadow-[0_0_20px_rgba(249,115,22,0.65)] group-focus-visible:shadow-[0_0_20px_rgba(249,115,22,0.65)]"
      />
      <span
        aria-hidden="true"
        className="text-sm font-medium text-gray-600 dark:text-gray-300"
      >
        {label}
      </span>
    </a>
  );
}

/**
 * Desktop counterpart of the store badges: one QR per available store so a
 * desktop visitor can get the app on their phone. Phones get badges instead,
 * and so does any viewport under the `md` breakpoint.
 */
export function StoreQrCodes({ className = '' }: StoreQrCodesProps) {
  const { t } = useTranslation('common');
  const { ios, android, anyAvailable } = getAppStoreConfig();

  if (!anyAvailable || isMobile()) return null;

  return (
    <div
      data-testid="store-qr-codes"
      className={`hidden md:block text-center ${className}`}
    >
      <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('nativeApp.qr.scan')}
      </p>
      <div className="flex flex-wrap justify-center gap-8">
        {ios.available && (
          <StoreQr
            store="ios"
            url={ios.url}
            label={t('storeButtons.appStore')}
          />
        )}
        {android.available && (
          <StoreQr
            store="android"
            url={android.url}
            label={t('storeButtons.googlePlay')}
          />
        )}
      </div>
    </div>
  );
}
